/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365 from '@msdyn365-commerce/core';
import {
    INotificationComponentsList,
    INotificationComponentsListState,
    INotificationComponentType,
    NotificationComponentsListsManager,
    NotificationEvents,
    NotificationFeedType
} from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions, ObjectExtensions, Random } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { INotificationsListProps } from './notifications-list.props.autogenerated';

/**
 * View for the notifications list which specifies the container and notifications items.
 */
export interface INotificationsListViewProps extends INotificationsListProps<{}> {
    container: IModuleProps;
    notificationsList: React.ReactNode;
}

/**
 * Configuration to make the list stickable to the page without overlapping any content on top.
 */
export interface INotificationComponentStickyConfiguration {
    /**
     * Minimum offset for top (default for the page).
     */
    minTopOffset: number;

    /**
     * Maximum offset for top (to limit the top offset while scrolling).
     */
    maxTopOffset: number;
}

/**
 * Stickable notification list state which defines the top position of the list at the moment.
 */
export interface IStickableNotificationComponentsListState extends INotificationComponentsListState {
    /**
     * The top position of the list on the page.
     */
    top?: number;
}

/**
 * A notification list which is operated by notification manager.
 * It is responsible for rendering the received notifications.
 */
export class NotificationComponentsList extends React.Component<INotificationsListProps<{}>, IStickableNotificationComponentsListState>
    implements INotificationComponentsList {
    /**
     * The class name to use.
     */
    private static readonly _className: string = 'msc-notifications-list';

    /**
     * The feed which the list is responsible for displaying.
     */
    public readonly feed: NotificationFeedType;

    /**
     * Configuration which specifies if and how the list should be stickable.
     * If undefined, the configuration will not be used at all. And list will not be stickable.
     */
    private readonly _stickyConfiguration?: INotificationComponentStickyConfiguration;

    /**
     * The list of notification which should be shown in the list at the moment.
     * @remarks Should be used to retrieve the latest list of notifications
     * as the list from the state is updated only after rendering.
     */
    private _notificationsComponentList: INotificationComponentType[];

    /**
     * Unique identifier of the list.
     * Uses format 'NotificationComponentsList-GUID';.
     */
    private _instanceId?: string;

    /**
     * Initializes instance with the given props.
     * @param props - The configuration of the list.
     */
    public constructor(props: INotificationsListProps<{}>) {
        super(props);

        this.feed = props.config.feed;

        if (
            !ObjectExtensions.isNullOrUndefined(props.config.minTopOffset) &&
            !ObjectExtensions.isNullOrUndefined(props.config.maxTopOffset)
        ) {
            this._stickyConfiguration = {
                minTopOffset: props.config.minTopOffset,
                maxTopOffset: props.config.maxTopOffset
            };
        }

        this._notificationsComponentList = [];

        this.state = {
            notificationsComponentList: []
        };

        this._handleTopUpdate = this._handleTopUpdate.bind(this);
        this._getCurrentTopPosition = this._getCurrentTopPosition.bind(this);
    }

    /**
     * Specifies if the component should be re-rendered.
     * @param nextProps - The next configuration the component is receiving.
     * @param nextState - The next state the component is receiving.
     * @returns True if component should be re-rendered, false if component should not be changed.
     */
    public shouldComponentUpdate(
        nextProps: Readonly<INotificationsListProps<{}>>,
        nextState: Readonly<IStickableNotificationComponentsListState>
    ): boolean {
        const isPropsDifferent =
            this.feed !== nextProps.config.feed ||
            this.props.config.maxNotificationsCount !== nextProps.config.maxNotificationsCount ||
            this.props.config.maxTopOffset !== nextProps.config.maxTopOffset ||
            this.props.config.minTopOffset !== nextProps.config.minTopOffset ||
            this.props.config.className !== nextProps.config.className ||
            this.props.id !== nextProps.id;
        const isStateDifferent =
            this.state.top !== nextState.top ||
            this.state.notificationsComponentList.length !== nextState.notificationsComponentList.length ||
            this.state.notificationsComponentList.every(value =>
                nextState.notificationsComponentList.find(otherValue => otherValue.props.notification.id === value.props.notification.id)
            );
        return isPropsDifferent || isStateDifferent;
    }

    /**
     * Adds the current instance to the global list of instances so that it becomes available for the notification manager.
     * In case the list is specified to be stickable, also subscribes to the scroll event to update the offsets.
     */
    public componentDidMount(): void {
        NotificationComponentsListsManager.subscribe(this);

        this._instanceId = `NotificationComponentsList-${Random.Guid.generateGuid()}`;

        if (MsDyn365.isBrowser && this._stickyConfiguration !== undefined) {
            window.addEventListener('scroll', this._handleTopUpdate);
        }
    }

    /**
     * Remove the instance from the global list of instances so that it becomes unavailable for the notification manager.
     * Unsubscribes itself from the notification events and scroll event.
     */
    public componentWillUnmount(): void {
        NotificationComponentsListsManager.unsubscribe(this);

        this._notificationsComponentList.forEach(component =>
            component.props.notification.events.getValue(NotificationEvents.RemovedFromList)?.unsubscribe(this._instanceId!)
        );

        if (MsDyn365.isBrowser && this._stickyConfiguration !== undefined) {
            window.removeEventListener('scroll', this._handleTopUpdate);
        }
    }

    /**
     * Adds a new notification to the list.
     * @param {INotificationComponentType} notificationComponent Data required to render the notification. @see INotificationComponentType.
     */
    public addNotificationComponent(notificationComponent: INotificationComponentType): void {
        if (this._instanceId === undefined) {
            return;
        }

        if (
            !ObjectExtensions.isNullOrUndefined(this.props.config.maxNotificationsCount) &&
            this._notificationsComponentList.length >= this.props.config.maxNotificationsCount
        ) {
            throw new Error('Notifications limit reached, cannot show more notifications.');
        }

        // Subscribe to the remove event to filter the removed notifications out from the list.
        notificationComponent.props.notification.events.getValue(NotificationEvents.RemovedFromList)?.subscribe({
            instanceId: this._instanceId,
            handler: () => {
                notificationComponent.props.notification.events
                    .getValue(NotificationEvents.RemovedFromList)
                    ?.unsubscribe(this._instanceId!);
                const newList = this._notificationsComponentList.filter(component => component !== notificationComponent);
                this._notificationsComponentList = newList;
                this.setState({ notificationsComponentList: this._notificationsComponentList });
            }
        });

        // Prepend the notification as newest should come to the top.
        this._notificationsComponentList.unshift(notificationComponent);
        this.setState({ notificationsComponentList: this._notificationsComponentList });
        this._handleTopUpdate();
    }

    public render(): React.ReactNode {
        const className = classnames(
            NotificationComponentsList._className,
            `${NotificationComponentsList._className}__${this.feed}`,
            this.props.config.className
        );

        const viewProps: INotificationsListViewProps = {
            ...this.props,
            container: {
                moduleProps: this.props,
                className,
                id: this._instanceId,
                style: { top: this.state.top }
            },
            notificationsList: this._renderNotificationsList()
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Renders the list of notifications.
     * @returns React node with a list of notifications.
     */
    private _renderNotificationsList(): React.ReactNode {
        const hasNotifications = ArrayExtensions.hasElements(this.state.notificationsComponentList);
        const containerClassName = classnames(
            `${NotificationComponentsList._className}__scrollable`,
            hasNotifications
                ? `${NotificationComponentsList._className}__has-notifications`
                : `${NotificationComponentsList._className}__empty`
        );

        return (
            <div className={containerClassName} style={{ maxHeight: this.state.top && `calc(100vh - ${this.state.top}px)` }}>
                {this.state.notificationsComponentList.map(component => {
                    const ComponentClass = component.componentClass;
                    return <ComponentClass {...component.props} key={component.props.notification.id} />;
                })}
            </div>
        );
    }

    /**
     * Retrieves current top offset position by looking at scroll position and rounding it to the stickable limits.
     */
    private _getCurrentTopPosition(): number | undefined {
        const configuration = this._stickyConfiguration;

        if (configuration === undefined) {
            return undefined;
        }

        let pageYOffset = 0;
        if (MsDyn365.isBrowser) {
            pageYOffset = window.pageYOffset;
        }

        return Math.max(Math.min(configuration.maxTopOffset, configuration.maxTopOffset - pageYOffset), configuration.minTopOffset);
    }

    /**
     * Updates the top offset on scroll event to make the list stickable.
     */
    private _handleTopUpdate(): void {
        const newTop = this._getCurrentTopPosition();
        this.setState({ top: newTop });
    }
}

export default NotificationComponentsList;
